/*
 * Timeline: events in order (activation history, ticket activity, payment attempts).
 *   <Timeline items={[{ id, title: …, description: …, time: iso, tone: 'success', icon: <Check /> }]} />
 * Times render relative ("3 hours ago") with the exact date in the tooltip/title.
 */
import type { ReactNode } from 'react'
import { cn } from '@/lib/cn'
import { useFormat } from '@/i18n/useFormat'
import { TONE_CLASSES, type Tone } from './Badge'

export type TimelineItem = {
  id: string
  title: ReactNode
  description?: ReactNode
  time?: string
  tone?: Tone
  icon?: ReactNode
}

export function Timeline({ items, className }: { items: TimelineItem[]; className?: string }) {
  const f = useFormat()
  return (
    <ol className={cn('flex flex-col', className)}>
      {items.map((item, i) => {
        const tone = TONE_CLASSES[item.tone ?? 'neutral']
        return (
          <li key={item.id} className="relative flex gap-3 pb-6 last:pb-0">
            {i < items.length - 1 ? (
              <span aria-hidden="true" className="absolute start-[13px] top-8 bottom-1 w-px bg-hairline" />
            ) : null}
            <span
              aria-hidden="true"
              className={cn(
                'relative grid size-7 shrink-0 place-items-center rounded-full ring-4 ring-surface [&_svg]:size-3.5',
                tone.pill,
              )}
            >
              {item.icon ?? <span className={cn('size-2 rounded-full', tone.dot)} />}
            </span>
            <div className="flex min-w-0 flex-1 flex-col gap-0.5 pt-0.5">
              <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                <p className="text-14 font-medium text-ink">{item.title}</p>
                {item.time ? (
                  <time
                    dateTime={item.time}
                    title={f.date(item.time, 'datetime')}
                    className="text-12 text-subtle"
                  >
                    {f.relative(item.time)}
                  </time>
                ) : null}
              </div>
              {item.description ? (
                <div className="text-13 leading-5 text-muted">{item.description}</div>
              ) : null}
            </div>
          </li>
        )
      })}
    </ol>
  )
}
